import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MEDICATION_CONCEPTS,
  MEDICATION_FORMS,
  DOSE_UNITS,
  type DoseCalcResult,
} from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table, Field, inputClass } from '../components/ui.js';
import { Select, FormGrid, QueryState, ErrorState } from '../components/form.js';

type DraftRow = {
  id: string;
  concept: string;
  roundedAmount: string;
  roundedUnit: string;
  rawDoseMg: string;
  boundedDoseMg: string;
  roundingReason: string;
  warnings: { code: string; messageKey: string; params?: Record<string, string> }[];
  weightStale: boolean;
  status: string;
  createdAt: string;
};
type PendingOrder = {
  id: string;
  concept: string;
  brandName: string | null;
  form: string;
  doseAmount: string | null;
  doseUnit: string | null;
};
type CalcResult = DoseCalcResult & { id: string };

export function DoseReview() {
  const { t } = useI18n();
  const queueQ = useQuery({
    queryKey: ['dose', 'review-queue'],
    queryFn: () => api.get<{ drafts: DraftRow[]; pendingOrders: PendingOrder[] }>('/dose/review-queue'),
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('dose.title')}</h1>

      <Calculator />

      <Card title={t('dose.reviewQueue')}>
        <p className="text-sm text-slate-500 mb-3">{t('dose.queueSubtitle')}</p>
        <QueryState
          isLoading={queueQ.isLoading}
          isError={queueQ.isError}
          error={queueQ.error}
          errorFallback={t('common.error')}
          isEmpty={(queueQ.data?.drafts.length ?? 0) === 0}
          emptyMessage="—"
        >
          <div className="space-y-3">
            {queueQ.data?.drafts.map((d) => (
              <DraftReview key={d.id} draft={d} pendingOrders={queueQ.data?.pendingOrders ?? []} />
            ))}
          </div>
        </QueryState>
      </Card>

      <Card title={t('dose.ordersPendingReview')}>
        <p className="text-sm text-slate-500 mb-3">{t('dose.ordersSubtitle')}</p>
        <QueryState
          isLoading={queueQ.isLoading}
          isError={queueQ.isError}
          error={queueQ.error}
          errorFallback={t('common.error')}
          isEmpty={(queueQ.data?.pendingOrders.length ?? 0) === 0}
          emptyMessage="—"
        >
          <Table headers={[t('concept.custom'), t('med.form'), t('med.dose')]}>
            {queueQ.data?.pendingOrders.map((o) => (
              <tr key={o.id} className="border-b border-slate-100">
                <td className="py-2 pr-4">{t(`concept.${o.concept}`)}{o.brandName ? ` (${o.brandName})` : ''}</td>
                <td className="py-2 pr-4">{t(`form.${o.form}`)}</td>
                <td className="py-2 pr-4">{o.doseAmount ? `${o.doseAmount} ${o.doseUnit ?? ''}` : '—'}</td>
              </tr>
            ))}
          </Table>
        </QueryState>
      </Card>
    </div>
  );
}

function Calculator() {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [weightGrams, setWeightGrams] = useState('');
  const [weightDate, setWeightDate] = useState(today);
  const [concept, setConcept] = useState('pyrimethamine');
  const [form, setForm] = useState('suspension');
  const [concentrationMgPerMl, setConcentration] = useState('');
  const [mgPerKg, setMgPerKg] = useState('');
  const [perDay, setPerDay] = useState(false);
  const [dosesPerDay, setDosesPerDay] = useState('');
  const [minDoseMg, setMinDoseMg] = useState('');
  const [maxDoseMg, setMaxDoseMg] = useState('');
  const [measurableIncrement, setIncrement] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [result, setResult] = useState<CalcResult | null>(null);

  const calcM = useMutation({
    mutationFn: () =>
      api.post<{ calculation: CalcResult }>('/dose/calculate', {
        weightGrams: weightGrams.trim(),
        weightDate,
        concept,
        form,
        concentrationMgPerMl: concentrationMgPerMl.trim() || null,
        formula: {
          mgPerKg: mgPerKg.trim(),
          perDay: perDay || undefined,
          dosesPerDay: dosesPerDay.trim() ? Number(dosesPerDay) : undefined,
          minDoseMg: minDoseMg.trim() || undefined,
          maxDoseMg: maxDoseMg.trim() || undefined,
        },
        formulaVersionId: 'manual',
        measurableIncrement: measurableIncrement.trim() || undefined,
        evaluationDate: today,
      }),
    onSuccess: (res) => setResult(res.calculation),
  });

  const qc = useQueryClient();

  // Live, approximate preview (the authoritative value comes from the server on Calculate).
  const isLiquid = ['suspension', 'compounded_liquid', 'drops'].includes(form);
  const kg = weightGrams.trim() && Number.isFinite(Number(weightGrams)) ? Number(weightGrams) / 1000 : null;
  const mk = mgPerKg.trim() && Number.isFinite(Number(mgPerKg)) ? Number(mgPerKg) : null;
  const dpd = dosesPerDay.trim() && Number(dosesPerDay) > 0 ? Number(dosesPerDay) : null;
  const totalMg = kg != null && mk != null ? kg * mk : null;
  const perDoseMg = totalMg != null ? (perDay && dpd ? totalMg / dpd : totalMg) : null;
  const fmt = (n: number) => (Math.round(n * 1000) / 1000).toString();
  const stepH = 'text-sm font-semibold text-slate-700 mb-2 mt-1';

  return (
    <Card title={t('dose.calculator')}>
      <p className="text-sm text-slate-500 mb-4">{t('dose.calcSubtitle')}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          calcM.mutate();
        }}
      >
        {/* Step 1 — weight */}
        <h3 className={stepH}>{t('dose.step1')}</h3>
        <FormGrid>
          <Field label={`${t('growth.weight')} (g)`}>
            <input className={inputClass} inputMode="decimal" value={weightGrams} onChange={(e) => setWeightGrams(e.target.value)} required />
            {kg != null && <span className="text-xs text-slate-400">= {fmt(kg)} kg</span>}
          </Field>
          <Field label={t('dose.weightDate')}>
            <input className={inputClass} type="date" value={weightDate} onChange={(e) => setWeightDate(e.target.value)} required />
          </Field>
        </FormGrid>

        {/* Step 2 — medication & form */}
        <h3 className={`${stepH} mt-4`}>{t('dose.step2')}</h3>
        <FormGrid>
          <Field label={t('med.concept')}>
            <Select value={concept} onChange={(e) => setConcept(e.target.value)}>
              {MEDICATION_CONCEPTS.map((c) => (
                <option key={c} value={c}>{t(`concept.${c}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('med.form')}>
            <Select value={form} onChange={(e) => setForm(e.target.value)}>
              {MEDICATION_FORMS.map((f) => (
                <option key={f} value={f}>{t(`form.${f}`)}</option>
              ))}
            </Select>
          </Field>
          {isLiquid ? (
            <Field label={t('med.concentration')}>
              <input className={inputClass} inputMode="decimal" value={concentrationMgPerMl} onChange={(e) => setConcentration(e.target.value)} placeholder="mg/mL" />
            </Field>
          ) : (
            <div className="flex items-end pb-1"><p className="text-xs text-slate-400">{t('dose.solidNote')}</p></div>
          )}
        </FormGrid>

        {/* Step 3 — dosing rule */}
        <h3 className={`${stepH} mt-4`}>{t('dose.step3')}</h3>
        <div className="mb-3">
          <span className="block text-sm font-medium text-slate-600 mb-1">{t('dose.amountBasis')}</span>
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
            <button type="button" onClick={() => setPerDay(false)} className={`px-3 py-1.5 text-sm ${!perDay ? 'bg-brand text-white' : 'bg-white text-slate-600'}`}>{t('dose.perDose')}</button>
            <button type="button" onClick={() => setPerDay(true)} className={`px-3 py-1.5 text-sm ${perDay ? 'bg-brand text-white' : 'bg-white text-slate-600'}`}>{t('dose.perDay')}</button>
          </div>
          <span className="block text-xs text-slate-400 mt-1">{perDay ? t('dose.perDayHint') : t('dose.perDoseHint')}</span>
        </div>
        <FormGrid>
          <Field label={t('dose.mgPerKg')}>
            <input className={inputClass} inputMode="decimal" value={mgPerKg} onChange={(e) => setMgPerKg(e.target.value)} required />
          </Field>
          {perDay && (
            <Field label={t('dose.dosesPerDay')}>
              <input className={inputClass} inputMode="numeric" value={dosesPerDay} onChange={(e) => setDosesPerDay(e.target.value)} />
            </Field>
          )}
        </FormGrid>

        {/* Live preview */}
        {perDoseMg != null && (
          <p className="text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-slate-600">
            {t('dose.preview')}: {fmt(kg!)} kg × {mgPerKg} mg/kg{perDay && dpd ? ` ÷ ${dpd}` : ''} ≈ <strong>{fmt(perDoseMg)} mg/{t('dose.perDoseShort')}</strong>
          </p>
        )}

        {/* Advanced — bounds & rounding */}
        <button type="button" className="text-sm underline text-brand mb-2" onClick={() => setShowAdvanced((s) => !s)}>
          {showAdvanced ? '▲' : '▼'} {t('dose.advanced')}
        </button>
        {showAdvanced && (
          <div className="mb-2">
            <FormGrid>
              <Field label={t('dose.measurableIncrement')}>
                <input className={inputClass} inputMode="decimal" value={measurableIncrement} onChange={(e) => setIncrement(e.target.value)} placeholder="0.1" />
              </Field>
              <Field label={t('dose.minDoseMg')}>
                <input className={inputClass} inputMode="decimal" value={minDoseMg} onChange={(e) => setMinDoseMg(e.target.value)} />
              </Field>
              <Field label={t('dose.maxDoseMg')}>
                <input className={inputClass} inputMode="decimal" value={maxDoseMg} onChange={(e) => setMaxDoseMg(e.target.value)} />
              </Field>
            </FormGrid>
            <p className="text-xs text-slate-400">{t('dose.incrementHint')}</p>
          </div>
        )}

        {calcM.isError && <div className="mb-3"><ErrorState error={calcM.error} fallback={t('common.error')} /></div>}
        <Button type="submit" disabled={calcM.isPending}>{t('dose.calculate')}</Button>
      </form>

      {result && <DraftResult result={result} mgPerKg={mgPerKg} onResolved={() => { setResult(null); void qc.invalidateQueries({ queryKey: ['dose', 'review-queue'] }); }} />}
    </Card>
  );
}

function DraftResult({ result, mgPerKg, onResolved }: { result: CalcResult; mgPerKg?: string; onResolved: () => void }) {
  const { t } = useI18n();
  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-sm text-amber-900">
        ⚠️ {t('dose.draftNotice')}
      </div>
      {/* Hero: the dose to give */}
      <div className="rounded-lg bg-brand-light/40 border border-brand/30 px-4 py-3 mb-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">{t('dose.give')}</div>
        <div className="text-2xl font-bold text-slate-800">{result.roundedAmount} {result.roundedUnit}</div>
        {/* Plain-language formula */}
        <div className="text-xs text-slate-500 mt-1">
          {result.weightKg} kg{mgPerKg ? ` × ${mgPerKg} mg/kg` : ''} = {result.rawDoseMg} mg → {result.roundedAmount} {result.roundedUnit}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-1">
        {t('dose.beforeRounding')}: {result.rawDoseMg} mg{result.boundedDoseMg !== result.rawDoseMg ? ` → ${result.boundedDoseMg} mg` : ''}
      </p>
      <p className="text-xs text-slate-500 mb-2">{result.roundingReason}</p>
      {result.warnings.length > 0 && (
        <ul className="space-y-1 mb-3">
          {result.warnings.map((w, i) => (
            <li key={i} className="text-sm">
              <Badge tone="warn">!</Badge> {t(w.messageKey, w.params)}
            </li>
          ))}
        </ul>
      )}
      <ApproveControls calculationId={result.id} defaultAmount={result.roundedAmount} defaultUnit={result.roundedUnit} onResolved={onResolved} />
    </div>
  );
}

function DraftReview({ draft, pendingOrders }: { draft: DraftRow; pendingOrders: PendingOrder[] }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <strong className="text-slate-800">{t(`concept.${draft.concept}`)}</strong>
        <span className="text-sm font-semibold">{draft.roundedAmount} {draft.roundedUnit}</span>
      </div>
      <p className="text-xs text-slate-500 mb-2">{draft.roundingReason}</p>
      {draft.warnings?.length > 0 && (
        <ul className="space-y-1 mb-2">
          {draft.warnings.map((w, i) => (
            <li key={i} className="text-sm"><Badge tone="warn">!</Badge> {t(w.messageKey, w.params)}</li>
          ))}
        </ul>
      )}
      <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2 text-xs text-amber-900">⚠️ {t('dose.draftNotice')}</div>
      <ApproveControls
        calculationId={draft.id}
        defaultAmount={draft.roundedAmount}
        defaultUnit={draft.roundedUnit}
        orderOptions={pendingOrders}
        onResolved={() => void qc.invalidateQueries({ queryKey: ['dose', 'review-queue'] })}
      />
    </div>
  );
}

function ApproveControls({
  calculationId,
  defaultAmount,
  defaultUnit,
  orderOptions,
  onResolved,
}: {
  calculationId: string;
  defaultAmount: string;
  defaultUnit: string;
  orderOptions?: PendingOrder[];
  onResolved: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'approve' | 'edit' | 'reject' | null>(null);
  const [editedAmount, setEditedAmount] = useState(defaultAmount);
  const [editedUnit, setEditedUnit] = useState(defaultUnit);
  const [reason, setReason] = useState('');
  const [orderId, setOrderId] = useState('');

  const m = useMutation({
    mutationFn: (decision: 'approve' | 'edit' | 'reject') =>
      api.post('/dose/approve', {
        calculationId,
        decision,
        editedAmount: decision === 'edit' ? editedAmount.trim() : undefined,
        editedUnit: decision === 'edit' ? editedUnit : undefined,
        reason: reason.trim() || undefined,
        orderId: orderId || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['medications'] });
      onResolved();
    },
  });

  return (
    <div>
      {orderOptions && orderOptions.length > 0 && (
        <Field label={`${t('interp.dose_change')} → ${t('dose.toOrder')}`}>
          <Select value={orderId} onChange={(e) => setOrderId(e.target.value)}>
            <option value="">{t('dose.recordOnly')}</option>
            {orderOptions.map((o) => (
              <option key={o.id} value={o.id}>{t(`concept.${o.concept}`)}{o.brandName ? ` (${o.brandName})` : ''}</option>
            ))}
          </Select>
        </Field>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => { setMode('approve'); m.mutate('approve'); }} disabled={m.isPending}>{t('common.approve')}</Button>
        <Button variant="secondary" onClick={() => setMode((x) => (x === 'edit' ? null : 'edit'))}>{t('interp.dose_change')}</Button>
        <Button variant="danger" onClick={() => setMode((x) => (x === 'reject' ? null : 'reject'))}>{t('common.reject')}</Button>
      </div>

      {mode === 'edit' && (
        <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
          <FormGrid>
            <Field label={t('dose.editedAmount')}>
              <input className={inputClass} inputMode="decimal" value={editedAmount} onChange={(e) => setEditedAmount(e.target.value)} />
            </Field>
            <Field label={t('dose.editedUnit')}>
              <Select value={editedUnit} onChange={(e) => setEditedUnit(e.target.value)}>
                {DOSE_UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </Select>
            </Field>
          </FormGrid>
          <Field label={`${t('med.reason')} (${t('dose.reasonRequired')})`}>
            <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} required />
          </Field>
          <Button onClick={() => m.mutate('edit')} disabled={m.isPending || !reason.trim()}>{t('common.save')}</Button>
        </div>
      )}

      {mode === 'reject' && (
        <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
          <Field label={t('med.reason')}>
            <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Button variant="danger" onClick={() => m.mutate('reject')} disabled={m.isPending}>{t('common.reject')}</Button>
        </div>
      )}

      {m.isError && <div className="mt-2"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
    </div>
  );
}
